import { Badge, Box, List, ListItem } from "@chakra-ui/react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import Address from "components/Shared/Address";
import { LabeledValueRow } from "components/Shared/LabeledValueTable";
import Link from "components/Shared/Link";
import MaxHeightExpander from "components/Shared/MaxHeightExpander";
import { BaseDistributionCenterName } from "components/Shared/OrderingPlatformDirectory/DistributionCenters/DistributionCenterName";
import { Url } from "utils/links";

const fragment = gql(/* GraphQL */ `
  fragment OrderingPlatformDetailsDistributionCenters on OrderingPlatform {
    id
    distributionCentersList(
      filter: { name: { isNull: false }, isArchived: { equalTo: false } }
      orderBy: NAME_ASC
    ) {
      id
      code
      address {
        id
        ...AddressComponentFields
      }
      ...DistributionCenterBaseNameFragment
    }
  }
`);

type Props = {
  getLink?: (
    orderingPlatformId: UUID,
    distributionCenterId: UUID,
  ) => Url | undefined;
  orderingPlatform: FragmentType<typeof fragment> | null | undefined;
};

export default function OrderingPlatformDetailsDistributionCentersField({
  getLink,
  orderingPlatform,
}: Props) {
  const op = getFragmentData(fragment, orderingPlatform);
  return (
    <LabeledValueRow
      label="Distribution Centers"
      value={
        op?.distributionCentersList?.length ? (
          <MaxHeightExpander>
            <List
              maxH="calc(100vh - 10rem)"
              sx={{ "[data-expanded=true] &": { overflowY: "auto" } }}
              fontSize=".875em"
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(10em, 1fr))"
              gap=".85em"
              pt={2}
              pb={4}
            >
              {op.distributionCentersList.map((dc) => {
                const dcName = (
                  <Box
                    as="span"
                    display="flex"
                    alignItems="baseline"
                    gap=".35em"
                  >
                    <BaseDistributionCenterName
                      distributionCenter={dc}
                      fontWeight={450}
                      lineHeight="normal"
                      flex="1 1 60%"
                    />
                    {dc.code && (
                      <Badge
                        fontFamily="mono"
                        ml="auto"
                        fontSize=".75em"
                        flex="0 10 auto"
                        minW="2em"
                        isTruncated
                      >
                        {dc.code}
                      </Badge>
                    )}
                  </Box>
                );
                const href = getLink?.(op.id, dc.id);
                return (
                  <ListItem
                    key={dc.id}
                    pl=".5em"
                    borderLeftWidth={3}
                    minH="2.5em"
                  >
                    {href ? <Link href={href}>{dcName}</Link> : dcName}
                    <Address
                      address={dc.address}
                      mt=".25em"
                      fontSize=".9em"
                      fontWeight={300}
                      lineHeight="normal"
                      isTruncated
                    />
                  </ListItem>
                );
              })}
            </List>
          </MaxHeightExpander>
        ) : null
      }
      isValueLoaded={!!op?.distributionCentersList}
      rowProps={{ gridColumn: "1/-1", order: 1 }}
    />
  );
}
